/**
 * TJS Runtime
 *
 * Monadic type checking at runtime:
 * - Functions validate inputs against __tjs metadata
 * - If any input is an error, pass it through (no work)
 * - Type mismatches return error objects
 * - Errors propagate through call chains
 *
 * This runtime is attached to globalThis.__tjs and shared across modules.
 */

import { RUNTIME_ABI } from './runtime-abi'
import { validate, s } from 'tosijs-schema'
import { functionMetaToJSONSchema } from './json-schema'
import {
  Type,
  isRuntimeType,
  Union,
  Exactly,
  Generic,
  Enum,
  FunctionPredicate,
  Nullable,
  Optional,
  TArray,
  // Built-in types
  TString,
  TNumber,
  TBoolean,
  TInteger,
  TPositiveInt,
  TNonEmptyString,
  TEmail,
  TUrl,
  TUuid,
  Timestamp,
  TimestampISO,
  LegalDate,
  // Built-in generics
  TPair,
  TRecord,
  // Portable predicate helpers (future AJS builtins)
  isValidUrl,
  isValidTimestamp,
  isValidISOTimestamp,
  isValidLegalDate,
  openInferredShapes,
} from '../types/Type'
import { unwrapBoxed } from '../unwrap-boxed'

// Re-export Type utilities for consumers
export {
  Type,
  isRuntimeType,
  Union,
  Exactly,
  Generic,
  Enum,
  FunctionPredicate,
  Nullable,
  Optional,
  TArray,
  TString,
  TNumber,
  TBoolean,
  TInteger,
  TPositiveInt,
  TNonEmptyString,
  TEmail,
  TUrl,
  TUuid,
  Timestamp,
  TimestampISO,
  LegalDate,
  TPair,
  TRecord,
  isValidUrl,
  isValidTimestamp,
  isValidISOTimestamp,
  isValidLegalDate,
}

// Version from package.json - injected at build time or imported
const pkg = require('../../package.json') as { version: string }

export const TJS_VERSION: string = pkg.version

export { RUNTIME_ABI }

/**
 * Well-known symbol for custom equality.
 *
 * Any object can implement `[tjsEquals](other)` to control how `==` / `Is()`
 * compares it. Useful for Proxies that should delegate equality to their target.
 *
 * Priority: tjsEquals symbol → .Equals method → structural comparison
 */
export const tjsEquals: unique symbol = Symbol.for('tjs.equals')

/**
 * Comparison projections: a type's own answer to "what am I, for comparison?"
 *
 * `unwrapBoxed` already does this for three types — a `String` instance compares as a
 * string, `Number` as a number, `Boolean` as a boolean. That is not a special case, it is
 * the ROOT of a chain, and this table is the layer above it: entries a program adds for
 * types the host does not describe.
 *
 *     extend Timestamp { asCompared() { return this.seconds * 1000 } }
 *
 * Registered by `extend`, which already emits `registerExtension(type, 'asCompared', fn)`.
 * Read by `Eq`, `Is` and `toBool` — all three, which is the point: an errored service result
 * that projects to `false` should make `if (result)` false, not just `result == false`.
 *
 * **Chain, not a flat table.** This is the GLOBAL layer. A module's own layer lives in its
 * emitted output (the inline comparators consult a file-local table), so one module's
 * projection cannot change another's — a single shared mutable type→behaviour table is
 * prototype pollution by another name, which is what `extend` exists to avoid.
 *
 * A module's parent is `globalThis`, never its importer: modules are a flat graph, so an
 * importer-scoped chain would make a module's semantics depend on which importer evaluated
 * first. See `docs/type-system-north-star.md`.
 */
const projections = new Map<string, (this: unknown) => unknown>()

/** The constructor name a projection is keyed by. */
function typeKeyOf(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined
  if (typeof value !== 'object') return undefined
  try {
    return (value as any).constructor?.name
  } catch {
    // A Proxy trap can throw. An object that cannot be asked its type has no projection.
    return undefined
  }
}

export function registerProjection(
  typeName: string,
  fn: (this: unknown) => unknown
): void {
  projections.set(typeName, fn)
}

/**
 * The value AS COMPARED — its projection, or the value unchanged.
 *
 * **Must project to a primitive, or to nothing** (number, string, boolean, null, undefined).
 * An object projection defers the question rather than answering it. `bigint` is excluded
 * deliberately: `1n === 1` is false, so two projections of the same type disagreeing on
 * number-vs-bigint would compare unequal for values that are equal — and nobody needs
 * nanosecond-exact `==`.
 *
 * A non-conforming projection is IGNORED, not thrown: a hook that breaks `==` for every
 * value is worse than one that does not apply.
 */
function asCompared(value: unknown): unknown {
  const key = typeKeyOf(value)
  if (key === undefined) return value
  let fn = projections.get(key)
  if (typeof fn !== 'function') {
    // The type's OWN declaration, beneath the registry: `class Foo { asCompared() {…} }`.
    // The registry is keyed by constructor NAME, which is a third party describing a type
    // it does not own — so a locally registered projection is an override and wins. This
    // layer is the type answering for itself.
    //
    // Duck-typed on the name, deliberately, and it lands on the right side of the line this
    // file already draws: `[tjsEquals]` and `.Equals` are dispatched the same way, because
    // a hook a type DECLARES ABOUT ITSELF is categorically different from a boxed primitive
    // intercepting a comparison it never agreed to join. Nobody writes `asCompared` by
    // accident — unlike `valueOf`, which every Date and Number already has, and which is
    // why `unwrapBoxed` reads the internal slot instead of calling it.
    //
    // It is also the only layer a Proxy can reach (#33). A Proxy has no internal slot and
    // slots are not forwarded to its target, so the slot read throws; and its
    // `constructor.name` is its target's, so registering it means claiming the key for
    // every value of that type in the process. A `get` trap can serve a method.
    try {
      const own = (value as { asCompared?: unknown }).asCompared
      if (typeof own === 'function') fn = own as (this: unknown) => unknown
    } catch {
      // A trap that throws on the probe is not a type describing itself.
      return value
    }
  }
  if (typeof fn !== 'function') return value
  let projected: unknown
  try {
    projected = fn.call(value)
  } catch {
    // A declared hook that throws is the author's bug, but it must not throw out of `==` —
    // this language's promise is that errors are returned, not thrown.
    return value
  }
  const t = typeof projected
  return projected === null ||
    projected === undefined ||
    t === 'number' ||
    t === 'string' ||
    t === 'boolean'
    ? projected
    : value
}

/**
 * Parse semver version string into components
 */
function parseVersion(version: string): {
  major: number
  minor: number
  patch: number
  prerelease: string
} {
  // Split the prerelease suffix OFF first. Splitting the whole string on '.' turned
  // '0.13.0-beta.1' into [0, 13, NaN, 1] — and because `NaN !== NaN`, two IDENTICAL
  // prerelease versions compared as different, so every module that installed the runtime
  // "upgraded" over the previous instance and discarded its flight-recorder records.
  const [core = '', prerelease = ''] = version.split('-', 2)
  const [major = 0, minor = 0, patch = 0] = core.split('.').map(Number)
  return { major, minor, patch, prerelease }
}

/**
 * Compare two version strings
 * Returns: -1 if a < b, 0 if equal, 1 if a > b
 */
export function compareVersions(a: string, b: string): -1 | 0 | 1 {
  const va = parseVersion(a)
  const vb = parseVersion(b)

  if (va.major !== vb.major) return va.major < vb.major ? -1 : 1
  if (va.minor !== vb.minor) return va.minor < vb.minor ? -1 : 1
  if (va.patch !== vb.patch) return va.patch < vb.patch ? -1 : 1

  // Same numbers — semver says a RELEASE outranks its own prerelease, and identical
  // prereleases are equal (the case whose absence caused the runtime to replace itself).
  if (va.prerelease === vb.prerelease) return 0
  if (!va.prerelease) return 1 // 0.13.0 > 0.13.0-beta.1
  if (!vb.prerelease) return -1
  return comparePrerelease(va.prerelease, vb.prerelease)
}

/**
 * Compare prerelease tags per semver §11: split on `.`, compare identifier by identifier,
 * NUMERIC identifiers numerically and alphanumeric ones lexically, with numeric ranking
 * below alphanumeric; a shorter run of identifiers ranks below a longer one that shares
 * its prefix.
 *
 * A plain string compare gets the common case exactly backwards — `'beta.2' > 'beta.10'`,
 * because `'2' > '1'` — so the tenth beta looked OLDER than the second. Combined with
 * `installRuntime`'s wholesale replacement, an older beta then "upgrades" over a newer one
 * and discards the flight recorder and any applied `configure()`. That is the same shape
 * as the bug this function was written to fix; it was fixed for the equality case and left
 * wrong for the ordering case.
 */
function comparePrerelease(a: string, b: string): -1 | 0 | 1 {
  const ap = a.split('.')
  const bp = b.split('.')
  for (let i = 0; i < Math.max(ap.length, bp.length); i++) {
    const x = ap[i]
    const y = bp[i]
    if (x === undefined) return -1 // 1.0.0-alpha < 1.0.0-alpha.1
    if (y === undefined) return 1
    if (x === y) continue
    const xn = /^\d+$/.test(x)
    const yn = /^\d+$/.test(y)
    if (xn && yn) return Number(x) < Number(y) ? -1 : 1
    if (xn !== yn) return xn ? -1 : 1 // numeric identifiers rank lower
    return x < y ? -1 : 1
  }
  return 0
}

/**
 * The first MonadicError CARRIED inside a failed argument — `{ x: err }`, `{ kids: [err] }`.
 *
 * Called only when a check has already FAILED, so the happy path never pays. Inline shape
 * checks found such an error member by member; a declared `Type` checks the whole value at
 * once, so `p: Pt` called with `{ x: err }` returned a NEW "Expected Pt" error and lost the
 * caller's (0.14.0 final review, m-2). Plain objects and arrays only, own DATA properties only
 * (a getter is never run), cycle-safe, and bounded: a failure path must not become a walk of
 * an arbitrarily large value. The inline runtime carries the same logic (`__carried`),
 * pinned by a differential test.
 */
export function findCarriedError(v: unknown): MonadicError | undefined {
  if (!v || typeof v !== 'object') return undefined
  const seen = new Set<unknown>()
  const queue: unknown[] = [v]
  // The bound is on KEYS VISITED, not nodes: one node can have a million keys, and a node
  // bound let a 1M-element argument cost ~200ms on the failure path (0.14.0 re-review).
  let budget = 4096
  for (let i = 0; i < queue.length && budget > 0; i++) {
    const o = queue[i] as any
    const proto = Object.getPrototypeOf(o)
    if (!Array.isArray(o) && proto !== Object.prototype && proto !== null)
      continue
    // An array by INDEX: `Object.keys` would materialise every key before the budget
    // could stop it.
    const keys: ArrayLike<string | number> = Array.isArray(o)
      ? { length: Math.min(o.length, budget + 1) }
      : Object.keys(o)
    for (let j = 0; j < keys.length; j++) {
      if (--budget < 0) return undefined
      const d = Object.getOwnPropertyDescriptor(
        o,
        Array.isArray(o) ? j : (keys as string[])[j]
      )
      if (!d || !('value' in d)) continue
      const x = d.value
      if (isMonadicError(x)) return x as MonadicError
      if (x && typeof x === 'object' && !seen.has(x)) {
        seen.add(x)
        queue.push(x)
      }
    }
  }
  return undefined
}

/**
 * Check if two versions are compatible (same major version)
 */
export function versionsCompatible(a: string, b: string): boolean {
  const va = parseVersion(a)
  const vb = parseVersion(b)
  // Semver: below 1.0 a MINOR bump is the breaking one, so 0.13 and 0.14 are not
  // interchangeable (0.14.0 final review, m-6).
  if (va.major === 0 || vb.major === 0)
    return va.major === vb.major && va.minor === vb.minor
  return va.major === vb.major
}

/**
 * MonadicError - Internal error type for monadic error propagation
 *
 * This extends Error so:
 * 1. It's a real Error with proper stack traces
 * 2. User code can't accidentally process it as data (unlike { $error: true })
 * 3. It flows through function calls via instanceof checks
 *
 * NOT exported to user code - they just see Error instances.
 */
class MonadicErrorImpl extends Error {
  /** Path where the error occurred, e.g., "src/file.ts:42:greet.name" */
  readonly path: string
  /** Expected type */
  readonly expected?: string
  /** Actual type received */
  readonly actual?: string
  /** TJS call stack (only in debug mode) - shows source locations */
  readonly callStack?: string[]
  /** Why the check failed (from predicate reason strings) */
  readonly reason?: string
  /**
   * The ARGUMENT the check refused, as it arrived: the `Error` a caller passed where a number
   * belonged, or, when a member failed, the whole object that was missing it (not the
   * member's value, which is usually just `undefined`: the argument is what was wrong). Set by
   * `received()`, NON-enumerable, so logging or `JSON.stringify` of the error never walks a
   * large or cyclic value.
   */
  declare readonly received?: unknown

  constructor(
    message: string,
    path: string,
    expected?: string,
    actual?: string,
    callStack?: string[],
    reason?: string
  ) {
    super(message)
    this.name = 'MonadicError'
    this.path = path
    this.expected = expected
    this.actual = actual
    this.callStack = callStack
    this.reason = reason
    // Maintains proper stack trace in V8 environments
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, MonadicErrorImpl)
    }
  }
}

/**
 * The shape-versioned global slot every TJS module resolves `MonadicError` through.
 *
 * Each emitted `.js` carries its own inline runtime so it works with no setup, which means N
 * modules previously declared N distinct `MonadicError` classes — a bundler cannot merge them
 * (they are separate top-level declarations, so scope hoisting renames rather than fuses), so
 * even inside ONE bundle `Object.getPrototypeOf(a) === Object.getPrototypeOf(b)` was false.
 *
 * Nothing was broken by that: `isMonadicError` is duck-typed precisely so identity survives a
 * module boundary. But `instanceof` was silently false across one, which is the first thing a
 * consumer reaches for and the last place they would look for the cause.
 *
 * `MonadicError` is the ONLY prelude member eligible for this. It is a plain data class — same
 * six constructor params, same order, same fields, same `name` as the inline copy — so
 * whichever module wins the slot is observationally identical to any other. `Type`,
 * `FunctionPredicate` and `typeError` are NOT identical to their inline stubs (see
 * `docs/runtime-fusion.md` §3), and sharing those would be a behaviour change wearing a size
 * optimisation as a costume.
 *
 * The canonical runtime resolves through the slot rather than always using its own class —
 * otherwise a module that loaded BEFORE the runtime would keep a different class and
 * `instanceof` would still be false, which is the idiomatic order (ES imports are hoisted).
 *
 * The key is versioned by SHAPE, not by package version: keying on the release would mint a
 * new slot every version and share nothing. Fields have only ever been added, so older code
 * reading a newer class simply ignores what it does not know. Bump `_1` only if a field is
 * removed or repurposed — then old and new libraries get separate slots instead of the newer
 * silently winning over code that cannot cope with it.
 */
const MONADIC_ERROR_SLOT = '__tjs_MonadicError_1'
const g = globalThis as any
export const MonadicError: typeof MonadicErrorImpl = (g[MONADIC_ERROR_SLOT] ??=
  MonadicErrorImpl)
/** The instance type, so `MonadicError` still works in type position. */
export type MonadicError = MonadicErrorImpl

/**
 * What the caller actually passed, said usefully.
 *
 * An array used to report bare `array`, so `f([1, 'bad', 3])` against `xs: [0]` produced
 * "Expected array of integer …, got array" — accurate, and no help at all. `T[]` is a
 * headline feature; the whole question is WHICH element is wrong.
 *
 * The element types are summarised from the value alone, so no expected-type plumbing is
 * needed at the error site: `array of number | string`. That names the intruder next to
 * the expectation, which is the pair a reader can act on.
 *
 * Bounded on purpose, in WORK as well as in output — a long array must neither build a long
 * message nor cost a long scan to describe. Stopping at four distinct kinds bounds only the
 * message: a homogeneous `number[]` never reaches four, so a ten-million-element
 * array was walked in full to conclude "array of number". This runs on the ERROR path,
 * where a validation failure inside a loop pays it on every iteration — a diagnostic that
 * becomes the performance problem. `…` marks a summary drawn from a sample, so the message
 * never claims to have looked at more than it did.
 */
const DESCRIBE_SCAN_LIMIT = 64

function describeActual(value: unknown): string {
  if (value === null) return 'null'
  if (Array.isArray(value)) {
    if (value.length === 0) return 'empty array'
    const kinds: string[] = []
    const n = Math.min(value.length, DESCRIBE_SCAN_LIMIT)
    for (let i = 0; i < n; i++) {
      const v = value[i]
      const k = v === null ? 'null' : Array.isArray(v) ? 'array' : typeof v
      if (!kinds.includes(k)) kinds.push(k)
      // `…` on THIS path too. Stopping at four kinds is also stopping early, and omitting
      // the marker here contradicted the invariant it was added to establish — the message
      // never claims to have looked at more than it did. `[1,'a',true,null,{},Symbol()]`
      // read as an exhaustive list after scanning 4 of 6.
      if (kinds.length === 4) {
        return `array of ${kinds.join(' | ')}${
          i + 1 < value.length ? ' …' : ''
        }`
      }
    }
    const sampled = value.length > DESCRIBE_SCAN_LIMIT ? ' …' : ''
    return `array of ${kinds.join(' | ')}${sampled}`
  }
  return typeof value
}

/**
 * Attach the refused value to a type error (`err.received`). A type error that says only
 * "got object" leaves its receiver guessing; the value itself is what it needs (an `Error`
 * the caller made, say, which a typed function refuses rather than passes along: only TJS's
 * own errors propagate). Non-enumerable and read-only.
 */
function received<E extends Error>(err: E, value: unknown): E {
  Object.defineProperty(err, 'received', { value, configurable: true })
  return err
}

/**
 * Create a type error for inline validation.
 *
 * Called ONLY when a type check fails — no overhead on the happy path. Returns a
 * MonadicError that propagates through the call chain; in debug mode it captures the TJS
 * call stack with source locations.
 *
 * (This doc comment used to sit above `describeActual`, two functions up, describing
 * parameters that function does not have. Three doc comments were stacked there, and the
 * one nearest the code was the only one about it.)
 *
 * @param path - Location of the error, e.g., "src/file.ts:42:greet.name"
 * @param expected - Expected type, e.g., "string"
 * @param value - The actual value that failed the check
 */
export function typeError(
  path: string,
  expected: string,
  value: unknown,
  reason?: string,
  /**
   * The ARGUMENT whose check failed, when `value` is one of its members. Propagation is
   * decided on the argument: a MonadicError passed where `{ a: 0 }` is expected passes the
   * object check and fails on `.a` — whose value is `undefined`, not the error.
   */
  root?: unknown
): MonadicError {
  // The value is ALREADY a TJS error: propagate it, unchanged and unrecorded — this is how
  // an error flows through a call chain. It is decided HERE, at the failed check, not by a
  // pre-check on every parameter: that returned ANY `Error` from ANY function before its
  // body ran, so `describe(e: Error)`, `isErr(x: unknown)` and every catch-handler helper
  // silently handed their argument back. A value that MATCHES the declared type now always
  // reaches the body; a plain `Error` that does not is an honest type error.
  // The ARGUMENT first, then the failing value itself: an error carried in an options bag
  // (`{ x: err }`) propagates too, the same way whichever form checked it.
  if (root !== undefined && isMonadicError(root)) return root
  if (isMonadicError(value)) return value
  const carried = findCarriedError(root !== undefined ? root : value)
  if (carried) return carried
  // `typeof []` is 'object', which made every array failure report "got object" — least
  // helpful exactly where arrays are a headline feature (`xs: [0]`). `Array.isArray` is
  // the only honest answer here.
  const actual = describeActual(value)
  // Capture call stack in debug mode (getStack returns [] if not in debug mode)
  const stack = config.callStacks || config.debug ? getStack() : undefined
  const msg = reason
    ? `Expected ${expected} for '${path}': ${reason}`
    : `Expected ${expected} for '${path}', got ${actual}`
  const err = received(
    new MonadicError(msg, path, expected, actual, stack, reason),
    root !== undefined ? root : value
  )

  // Track in the flight recorder (zero cost on happy path)
  recorder.record({
    source: 'type',
    severity: 'error',
    message: msg,
    error: err,
    stack,
  })

  // Log to console if configured (includes source location from path)
  if (config.logTypeErrors) {
    console.error(`[TJS TypeError] ${err.message}`)
  }

  // Throw instead of returning if configured (for debugging)
  if (config.throwTypeErrors) {
    throw err
  }

  return err
}

/**
 * Check if a value is a MonadicError (for internal use)
 */
export function isMonadicError(value: unknown): value is MonadicError {
  // Duck-type check instead of instanceof — works across module boundaries,
  // inline runtimes, and different copies of the MonadicError class
  return (
    value instanceof Error &&
    (value as any).name === 'MonadicError' &&
    'path' in value
  )
}

/**
 * Error marker - identifies TJS error objects
 * @deprecated Use MonadicError instead. This interface is kept for backward compatibility.
 */
export interface TJSError {
  $error: true
  message: string
  /** Failure location - e.g., "greet.name" */
  path?: string
  /** Call stack in debug mode - e.g., ["main", "processUser", "greet.name"] */
  stack?: string[]
  expected?: string
  actual?: string
  /** Why the check failed (from predicate reason strings) */
  reason?: string
  cause?: Error | TJSError
  /** Source location for error reporting */
  loc?: { start: number; end: number }
  /** Multiple errors (when composing parameter errors) */
  errors?: TJSError[]
}

/**
 * Safety levels for runtime validation
 * - 'none': No validation unless explicitly forced with (?) or -?
 * - 'inputs': Validate inputs only (default) - outputs only with explicit -> or -?
 * - 'all': Validate both inputs and outputs unless explicitly skipped with (!) or -!
 */
export type SafetyLevel = 'none' | 'inputs' | 'all'

/**
 * Runtime configuration
 */
export interface TJSConfig {
  /** Enable debug mode - captures call stacks in errors */
  debug?: boolean
  /** Safety level for validation (default: 'inputs') */
  safety?: SafetyLevel
  /** Require explicit return types (error if -> not specified) */
  requireReturnTypes?: boolean
  /** Track call stacks for error diagnostics (default: false).
   *  Useful for server-side logging and agent debugging without devtools.
   *  Uses a fixed ring buffer — no allocation pressure. */
  callStacks?: boolean
  /** Ring buffer size for call stack tracking (default: 64) */
  maxStackSize?: number
  /** Track recent type errors in a ring buffer (default: true).
   *  Zero cost on happy path — only writes when an error occurs.
   *  Lets you catch monadic errors that were silently ignored. */
  trackErrors?: boolean
  /** Ring buffer size for error tracking (default: 64) */
  maxErrors?: number
  /** Log type errors to console.error when they occur (default: false) */
  logTypeErrors?: boolean
  /** Throw type errors instead of returning them (default: false).
   *  Useful for debugging — makes type errors impossible to ignore. */
  throwTypeErrors?: boolean
}

/** Default configuration values */
const DEFAULT_CONFIG: TJSConfig = {
  debug: false,
  safety: 'inputs',
  requireReturnTypes: false,
  callStacks: false,
  maxStackSize: 64,
  trackErrors: true,
  maxErrors: 64,
}

/** Current runtime configuration */
let config: TJSConfig = { ...DEFAULT_CONFIG }

/**
 * Late-configure guard (#23). A converted module snapshots its config when it
 * captures the runtime (`globalThis.__tjs.createRuntime()` at module-eval time),
 * so `configure()` called *after* the module graph has evaluated reaches nothing
 * — a silent no-op that made tosijs's debug/safe bundles inert. We can't fix
 * that silently in a patch (making config a live global read changes the
 * intentional per-instance isolation), but a silent no-op is the worst outcome,
 * so warn loudly instead.
 *
 * `modulesCaptured` distinguishes an emitted module's capture from the install:
 * during `globalThis.__tjs = createRuntime()` the RHS runs while `globalThis.__tjs`
 * is still undefined; an emitted `globalThis.__tjs.createRuntime()` runs while it
 * is already set. So a createRuntime call that sees an installed global IS a
 * post-install capture. Configuring after that can't reach it.
 */
let modulesCaptured = false
let warnedLateConfigure = false

/**
 * The `createRuntime` reachable through an INSTALLED `globalThis.__tjs`.
 *
 * An emitted module captures its runtime with `globalThis.__tjs.createRuntime()` at
 * module-eval time, so every route to that property must record the capture — otherwise a
 * later `configure()` stays silent about not reaching the module, which is the exact silent
 * no-op #23 exists to make audible.
 *
 * It used to be an inline arrow on the object returned by `createRuntime()`, so the flag was
 * set only when the global was installed as `globalThis.__tjs = createRuntime()`. The other
 * documented form — `installRuntime()`, and the primary one — installs the `runtime` singleton
 * below, which carried the BARE `createRuntime`. So on the main path the guard never fired and
 * the warning it exists to produce was unreachable. Measured against two emitted modules
 * loaded in Node: form two warned, `installRuntime()` said nothing.
 *
 * One function, referenced by both, so the two paths cannot drift again.
 */
const capturingCreateRuntime = () => {
  modulesCaptured = true
  return createRuntime()
}

function warnLateConfigureOnce(): void {
  if (warnedLateConfigure || !modulesCaptured) return
  warnedLateConfigure = true
  const msg =
    'TJS: configure() was called after a converted module already captured the ' +
    'runtime — the change will NOT reach modules that already evaluated (their ' +
    'config was snapshotted at import). Configure before importing converted ' +
    'modules (e.g. an import-order-first `configure-tjs` module). See ' +
    'https://github.com/tonioloewald/tjs-lang/issues/23'
  try {
    console.warn(msg)
  } catch {
    // never let the warning break the program
  }
  recorder.record({ source: 'app', severity: 'warning', message: msg })
}

/** Ring buffer for call stack tracking — fixed size, zero allocation */
const STACK_SIZE = 64
const callStackBuffer: string[] = new Array(STACK_SIZE).fill('')
let callStackHead = 0
let callStackCount = 0

/** Ring buffer for error history — zero cost on happy path */
const ERROR_BUF_SIZE = 64

/** Where a record came from. Open-ended: subsystems may add their own. */
export type RecordSource =
  | 'type'
  | 'vm'
  | 'wasm'
  | 'predicate'
  | 'app'
  | (string & {})

/**
 * How bad it is.
 *
 * `error` — something went wrong. Only these show up in `errors()`.
 * `warning` — something degraded; probably wanted, definitely not fatal.
 * `notice` — something worth knowing *after the fact*. May well be a false
 *   alarm. A wasm block falling back to JS is fine right up until the day your
 *   perf claim is a lie — so it gets recorded, not raised.
 */
export type RecordSeverity = 'error' | 'warning' | 'notice'

/** One entry in the flight recorder. */
export interface TJSRecord {
  source: RecordSource
  severity: RecordSeverity
  message: string
  /** The MonadicError, when this record wraps one (severity 'error'). */
  error?: MonadicError
  /** Structured payload — whatever the source thinks a reader will want. */
  data?: unknown
  /** Call stack, when `callStacks`/`debug` is on. */
  stack?: string[]
}

/** Filter for `records()`. Omitted fields match everything. */
export interface RecordFilter {
  source?: RecordSource
  severity?: RecordSeverity
}

/**
 * Sink a recorder copies each entry to. Receives the calling recorder's own
 * `record` so it can refuse to mirror into itself — see `globalMirror`.
 */
type Mirror = (entry: TJSRecord, selfRecord: (e: TJSRecord) => void) => void

/**
 * Instance recorders mirror into the installed global runtime.
 *
 * `createRuntime()` deliberately isolates config/stack/errors so transpiled
 * modules don't leak state into each other — but a page with three TJS modules
 * would then have three separate black boxes, and "what happened before the
 * crash?" has no single answer. So an instance keeps its own ring (isolated
 * `errors()`, unchanged semantics) *and* reports to the global recorder, which
 * is the one place that sees the whole flight.
 *
 * Guarded three ways:
 *  - no global runtime, or no `record` on it → no-op
 *  - the global IS this recorder (someone did `globalThis.__tjs = createRuntime()`)
 *    → skip, or `record` would call itself forever
 *  - a throwing global can't take the program down with it — a recorder that
 *    changes behavior is not a recorder
 */
const globalMirror: Mirror = (entry, selfRecord) => {
  const g = (globalThis as any).__tjs
  if (!g || typeof g.record !== 'function' || g.record === selfRecord) return
  try {
    g.record(entry)
  } catch {
    // Never let recording break the program it is recording.
  }
}

/**
 * The flight recorder: a bounded ring of everything the runtime noticed.
 *
 * Built as a factory because there are two of these — the module-level
 * singleton, and a private one per `createRuntime()` instance. They were once
 * two hand-maintained copies of the same ring; a recorder with two
 * implementations is a recorder with holes, so both now come from here.
 *
 * Bias: record liberally. A false alarm costs one ring slot. A missing entry
 * costs a debugging session with no evidence.
 */
function createRecorder(readConfig: () => TJSConfig, mirror?: Mirror) {
  const buffer: (TJSRecord | null)[] = new Array(ERROR_BUF_SIZE).fill(null)
  let head = 0
  let count = 0
  let errorTotal = 0
  let recordTotal = 0

  const size = () => readConfig().maxErrors ?? ERROR_BUF_SIZE

  /** All buffered records, oldest first. */
  const all = (): TJSRecord[] => {
    if (readConfig().trackErrors === false || count === 0) return []
    const n = size()
    const result: TJSRecord[] = []
    const start = (head - count + n) % n
    for (let i = 0; i < count; i++) {
      const entry = buffer[(start + i) % n]
      if (entry) result.push(entry)
    }
    return result
  }

  const api = {
    record(entry: TJSRecord): void {
      // Zero cost when off — this is the happy path for every typeError call.
      if (readConfig().trackErrors === false) return
      const n = size()
      buffer[head] = entry
      head = (head + 1) % n
      if (count < n) count++
      recordTotal++
      // Counts what errors() returns, and nothing else. A VM AgentError is
      // severity 'error' but carries no MonadicError, so it lands in records()
      // without inflating getErrorCount() — otherwise getErrorCount() and
      // errors().length would disagree, and both are public.
      if (entry.severity === 'error' && entry.error) errorTotal++
      mirror?.(entry, api.record)
    },

    /**
     * Type errors only — the documented `clearErrors()` → run → `errors()`
     * idiom depends on notices NOT showing up here. Widening this would
     * silently break every "no unexpected errors" test in the wild.
     */
    errors(): MonadicError[] {
      const result: MonadicError[] = []
      for (const entry of all()) {
        if (entry.severity === 'error' && entry.error) result.push(entry.error)
      }
      return result
    },

    records(filter?: RecordFilter): TJSRecord[] {
      const entries = all()
      if (!filter) return entries
      return entries.filter(
        (e) =>
          (filter.source === undefined || e.source === filter.source) &&
          (filter.severity === undefined || e.severity === filter.severity)
      )
    },

    clear(): TJSRecord[] {
      const cleared = all()
      head = 0
      count = 0
      errorTotal = 0
      recordTotal = 0
      return cleared
    },

    getErrorCount: () => errorTotal,
    getRecordCount: () => recordTotal,

    /**
     * Records overwritten by ring wrap since the last clear. A liberal
     * recorder wraps often, and "you lost N entries" must be legible rather
     * than silent — otherwise the black box lies by omission.
     */
    getDroppedCount: () => Math.max(0, recordTotal - count),

    reset(): void {
      head = 0
      count = 0
      errorTotal = 0
      recordTotal = 0
    },
  }

  return api
}

/**
 * The module-level recorder. It mirrors into the installed global too, unless that global IS
 * this module (`installRuntime()`): with `globalThis.__tjs = createRuntime()`, the documented
 * setup, what the module itself records (a `wrap()` notice) went into a ring nothing read.
 * Instances already mirrored; this was the one sink without.
 */
const moduleMirror: Mirror = (entry) => {
  const g = (globalThis as any).__tjs
  if (!g || typeof g.record !== 'function' || g.record === record) return
  try {
    g.record(entry)
  } catch {
    // Never let recording break the program it is recording.
  }
}
const recorder = createRecorder(() => config, moduleMirror)

/** Unsafe mode depth - when > 0, skip validation in wrap() */
let unsafeDepth = 0

/**
 * Enter unsafe mode - disables validation for all wrapped function calls
 * Can be nested (uses depth counter)
 */
export function enterUnsafe(): void {
  unsafeDepth++
}

/**
 * Exit unsafe mode - re-enables validation when depth returns to 0
 */
export function exitUnsafe(): void {
  if (unsafeDepth > 0) unsafeDepth--
}

/**
 * Check if currently in unsafe mode
 */
export function isUnsafeMode(): boolean {
  return unsafeDepth > 0
}

/**
 * Configure TJS runtime
 */
export function configure(options: TJSConfig): void {
  config = { ...config, ...options }
  warnLateConfigureOnce()
}

/**
 * Get current configuration
 */
export function getConfig(): TJSConfig {
  return { ...config }
}

/**
 * Push a function onto the call stack ring buffer.
 * Only tracks when callStacks or debug is enabled.
 * O(1), no allocation.
 */
export function pushStack(name: string): void {
  if ((config.callStacks || config.debug) && name) {
    const size = config.maxStackSize ?? STACK_SIZE
    callStackBuffer[callStackHead] = name
    callStackHead = (callStackHead + 1) % size
    if (callStackCount < size) callStackCount++
  }
}

/**
 * Pop a function from the call stack ring buffer.
 * O(1), no allocation.
 */
export function popStack(): void {
  if ((config.callStacks || config.debug) && callStackCount > 0) {
    const size = config.maxStackSize ?? STACK_SIZE
    callStackHead = (callStackHead - 1 + size) % size
    callStackCount--
  }
}

/**
 * Get current call stack snapshot (most recent entries, newest last)
 */
export function getStack(): string[] {
  if (callStackCount === 0) return []
  const size = config.maxStackSize ?? STACK_SIZE
  const result: string[] = []
  const start = (callStackHead - callStackCount + size) % size
  for (let i = 0; i < callStackCount; i++) {
    result.push(callStackBuffer[(start + i) % size])
  }
  return result
}

/**
 * Get recent type errors (newest last).
 * Only tracks when trackErrors is enabled (default: true).
 *
 * Type errors ONLY — warnings and notices live in `records()`. Keeping this
 * narrow preserves the documented idiom:
 *
 *   clearErrors(); runMyCode(); if (errors().length) // something failed
 */
export function errors(): MonadicError[] {
  return recorder.errors()
}

/**
 * Everything the runtime noticed (newest last) — the flight recorder.
 *
 * Unlike `errors()`, this includes warnings and notices: a wasm block that fell
 * back to JS, a typed array copied on every call, a predicate that ran
 * interpreted. Individually these may be false alarms; after an incident they
 * are the evidence.
 *
 *   records()                            // everything
 *   records({ source: 'wasm' })          // just wasm
 *   records({ severity: 'notice' })      // just the near-misses
 */
export function records(filter?: RecordFilter): TJSRecord[] {
  return recorder.records(filter)
}

/**
 * Record an event. Never throws, never changes behavior — if writing to the
 * recorder could alter the outcome, it would not be a recorder.
 */
export function record(entry: TJSRecord): void {
  recorder.record(entry)
}

/**
 * Clear error history. Returns the cleared errors (type errors only, for
 * back-compat); clears the whole recorder, since it is one ring.
 */
export function clearErrors(): MonadicError[] {
  const cleared = recorder.errors()
  recorder.clear()
  return cleared
}

/** Clear the recorder. Returns everything it held. */
export function clearRecords(): TJSRecord[] {
  return recorder.clear()
}

/**
 * Total *error* count since last clear (may exceed buffer size).
 */
export function getErrorCount(): number {
  return recorder.getErrorCount()
}

/** Total records of every severity since last clear (may exceed buffer size). */
export function getRecordCount(): number {
  return recorder.getRecordCount()
}

/** Records lost to ring wrap since the last clear. */
export function getDroppedCount(): number {
  return recorder.getDroppedCount()
}

/**
 * Reset runtime state to defaults
 *
 * Resets: config, callStack, errors, unsafeDepth
 * Use this in test teardown to prevent state leaking between tests.
 */
export function resetRuntime(): void {
  config = { ...DEFAULT_CONFIG }
  callStackHead = 0
  callStackCount = 0
  recorder.reset()
  unsafeDepth = 0
  modulesCaptured = false
  warnedLateConfigure = false
}

/**
 * Structural equality - the == that works
 *
 * Rules:
 * 1. If left has [tjsEquals], call left[tjsEquals](right)
 * 2. If right has [tjsEquals], call right[tjsEquals](left)
 * 3. If left has .Equals, call left.Equals(right)
 * 4. If right has .Equals, call right.Equals(left)
 * 5. Arrays/objects: recursive structural comparison
 * 6. Primitives: strict equality (no coercion)
 *
 * Usage: `a Is b` transforms to `Is(a, b)`
 */
export function Is(a: unknown, b: unknown): boolean {
  return goIs(a, b, 0, null)
}

/**
 * #21: past MEMO_DEPTH, memoize visited (a, b) pairs and treat a revisit as
 * equal. Without this, shared-reference graphs (DAGs — O(depth) nodes,
 * 2^depth unfolded paths) took exponential time (~61s at depth 30), and
 * DISTINCT-but-cyclic graphs recursed forever (stack overflow).
 *
 * Assuming true on revisit is sound (coinductive/bisimulation equality): any
 * `false` short-circuits every `.every`/loop straight to the top, so a pair
 * found in the memo either already returned true or is still being proven
 * higher in this same stack.
 *
 * The depth threshold keeps the hot path free: flat and typically-nested
 * compares (depth < MEMO_DEPTH) never allocate and never touch the memo —
 * measured unchanged vs pre-fix (~29ns flat / ~58ns nested per call). A
 * shallow shared graph pays at most a 2^MEMO_DEPTH constant factor over
 * linear (negligible); exponential blowup NEEDS depth, and past the
 * threshold every branch is memoized, so total work stays bounded. Cycles
 * grow depth unconditionally, so they always cross the threshold and
 * terminate.
 *
 * Same defect class exists in four sibling copies (deliberate bundle-isolated
 * duplicates): tests.ts expectFunction, js-tests.ts __deepEqual/formatValue,
 * and the emitted inline Is in emitters/js.ts. Fix all five together.
 */
const MEMO_DEPTH = 8

function goIs(
  a: unknown,
  b: unknown,
  depth: number,
  memo: WeakMap<object, WeakSet<object>> | null
): boolean {
  // Custom-equality protocol, in priority order: [tjsEquals], then .Equals (back-compat).
  //
  // PROBING is fail-soft; INVOKING is not, and the distinction is deliberate. Merely
  // asking "does this object declare custom equality?" reads a property, and on a Proxy
  // that runs a trap which can throw — so a hostile object could throw a raw exception out
  // of `Is`, in a language whose promise is that errors are returned rather than thrown.
  // An object that cannot be asked whether it has a custom equality does not have one.
  //
  // A hook that IS declared and then throws is different: that is the author's own code,
  // opted into by declaring it, and swallowing its failure would hide a real bug in it.
  const customEquals = (x: unknown): ((other: unknown) => boolean) | null => {
    if (x === null || typeof x !== 'object') return null
    try {
      const viaSymbol = (x as any)[tjsEquals]
      if (typeof viaSymbol === 'function') return viaSymbol.bind(x)
      const viaMethod = (x as any).Equals
      if (typeof viaMethod === 'function') return viaMethod.bind(x)
    } catch {
      return null
    }
    return null
  }
  const aEquals = customEquals(a)
  if (aEquals) return aEquals(b)
  const bEquals = customEquals(b)
  if (bEquals) return bEquals(a)

  // Unwrap boxed primitives (new String, new Number, new Boolean) so structural equality
  // honors the intent: new String('foo') == 'foo'. Via the internal slot, like `Eq` and
  // `toBool` — a `.valueOf()` call here let a boxed subclass run user code inside a
  // structural comparison, and a throwing one threw out of `Is`.
  //
  // NOTE the deliberate ORDER: the `[tjsEquals]` / `.Equals` protocol dispatch above runs
  // FIRST and is untouched. Those are opt-in hooks a type declares about itself, which is
  // categorically different from a boxed primitive intercepting a comparison it never
  // agreed to participate in.
  // Projections apply at EVERY node, which is why `asCompared` is a projection rather
  // than an `equals(other)` predicate: a Timestamp nested three levels down just works.
  a = unwrapBoxed(asCompared(a))
  b = unwrapBoxed(asCompared(b))

  // Identical references or primitives
  if (a === b) return true

  // NaN === NaN (JS gets this wrong)
  if (
    typeof a === 'number' &&
    typeof b === 'number' &&
    isNaN(a as number) &&
    isNaN(b as number)
  ) {
    return true
  }

  // null and undefined are equal to each other (nullish equality)
  // This preserves the useful JS pattern: x == null checks for both
  if ((a === null || a === undefined) && (b === null || b === undefined)) {
    return true
  }

  // If only one is nullish, not equal
  if (a === null || a === undefined || b === null || b === undefined) {
    return false
  }

  // Different types - not equal (no coercion)
  if (typeof a !== typeof b) return false

  // Primitives that aren't === are not equal
  if (typeof a !== 'object') return false

  // Both sides are objects from here on. Past the threshold, memoize the pair
  // (see MEMO_DEPTH above) — revisit ⇒ equal.
  if (depth >= MEMO_DEPTH) {
    if (memo === null) memo = new WeakMap()
    let set = memo.get(a as object)
    if (set) {
      if (set.has(b as object)) return true
    } else {
      set = new WeakSet()
      memo.set(a as object, set)
    }
    set.add(b as object)
  }

  // Sets — order-independent element equality
  if (a instanceof Set && b instanceof Set) {
    if ((a as Set<unknown>).size !== (b as Set<unknown>).size) return false
    for (const v of a as Set<unknown>) {
      if (!(b as Set<unknown>).has(v)) return false
    }
    return true
  }

  // Maps — key-value structural equality
  if (a instanceof Map && b instanceof Map) {
    if ((a as Map<unknown, unknown>).size !== (b as Map<unknown, unknown>).size)
      return false
    for (const [k, v] of a as Map<unknown, unknown>) {
      if (!(b as Map<unknown, unknown>).has(k)) return false
      if (!goIs(v, (b as Map<unknown, unknown>).get(k), depth + 1, memo))
        return false
    }
    return true
  }

  // Dates — compare timestamps
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime()
  }

  // RegExps — compare string representation
  if (a instanceof RegExp && b instanceof RegExp) {
    return a.toString() === b.toString()
  }

  // Arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((v, i) => goIs(v, b[i], depth + 1, memo))
  }
  if (Array.isArray(a) !== Array.isArray(b)) return false

  // Objects - structural comparison
  const keysA = Object.keys(a as object)
  const keysB = Object.keys(b as object)
  if (keysA.length !== keysB.length) return false
  return keysA.every((k) => goIs((a as any)[k], (b as any)[k], depth + 1, memo))
}

/**
 * Structural inequality - the != that works
 *
 * Usage: `a IsNot b` transforms to `IsNot(a, b)`
 */
export function IsNot(a: unknown, b: unknown): boolean {
  return !Is(a, b)
}

/**
 * Honest equality — what == should have been.
 *
 * Like === but fixes the two remaining footguns:
 * 1. Unwraps boxed primitives: new String('foo') Eq 'foo' → true
 * 2. Nullish equality: null Eq undefined → true
 *
 * Does NOT do deep structural comparison on objects/arrays (that's O(n)).
 * Use Is/IsNot for explicit structural comparison when you need it.
 *
 * Usage: `a == b` with TjsEquals transforms to `Eq(a, b)`
 */

/**
 * Honest typeof — fixes `typeof null === 'object'`
 *
 * Returns `'null'` for null instead of `'object'`.
 * All other values return the same as standard typeof.
 *
 * Usage: `typeof x` with TjsEquals transforms to `TypeOf(x)`
 */
export function TypeOf(value: unknown): string {
  if (value === null) return 'null'
  return typeof value
}

/**
 * Honest boolean coercion. Like `Boolean(x)` but unwraps boxed primitives
 * first, fixing the JS footgun `Boolean(new Boolean(false)) === true`.
 *
 * Under TjsStandard, the source rewriter wraps every truthiness context
 * (if/while/for/do-while conditions, `!`, `&&`, `||`, ternary, and
 * top-level `Boolean(x)` calls) with this function so a boxed `false`
 * actually behaves as `false`.
 */
export function toBool(value: unknown): boolean {
  // Same slot-read discipline as `Eq`, and it matters MORE here: `toBool` is injected at
  // every truthiness site in a `.tjs` file, so its reach is far wider than `==`'s. Calling
  // `value.valueOf()` let a boxed subclass run arbitrary user code inside every `if`, and
  // a throwing `valueOf` threw out of every `if`. The two disagreed outright: with a plain
  // subclass, `Eq(e, false)` was true with zero user code run while `toBool(e)` ran user
  // code and returned the opposite.
  // `asCompared` first: a type that says it compares as `false` must be FALSY in an `if`,
  // not merely equal to `false`. An errored service result is an object and objects are
  // truthy, so without this the type has no way to say otherwise.
  return Boolean(unwrapBoxed(asCompared(value)))
}

/**
 * The sentinel `NaN` maps to, so `case NaN:` can match under `===`.
 *
 * `Eq(NaN, NaN)` is true — one of the footguns TJS fixes — but `switch` still dispatches
 * with `===` underneath, and `NaN === NaN` is false. Mapping both sides to one symbol makes
 * the two agree without giving up the jump table.
 */
export const swNaN: unique symbol = Symbol.for('tjs.switch.NaN')

/**
 * The comparison KEY of a value, for `switch` in native `.tjs` (#43).
 *
 * `switch` is an equality construct, and it was the only one still using `===` — the one
 * TJS exists to fix — with no alternative spelling to reach for. With `==` you can at least
 * write `===`; with `switch` the options were "restructure into an if-chain" or "coerce the
 * discriminant by hand", both of which are the ceremony `Eq` removes.
 *
 * This normalises the DISCRIMINANT (and any non-literal case) rather than comparing each arm
 * with `Eq`, so literal cases stay literal and the engine's jump table survives — see
 * `switch-transform.ts` for why that trade decides the design. The result agrees with `Eq`
 * exactly: same `asCompared` → `unwrapBoxed` chain, plus the two clauses `===` cannot
 * express on its own (`undefined` and `null` are one key; `NaN` matches itself).
 */
export function swKey(value: unknown): unknown {
  const v = unwrapBoxed(asCompared(value))
  if (v === undefined) return null
  if (typeof v === 'number' && v !== v) return swNaN
  return v
}

export function Eq(a: unknown, b: unknown): boolean {
  // Unwrap boxed primitives via the PROTOTYPE method, not `a.valueOf()`.
  //
  // A subclass can override `valueOf`, and calling it would let a comparison run arbitrary
  // user code — it could throw, mutate, or simply lie about the value. `==` has exactly
  // that hazard for every object, and `Eq` exists to be the safe path, so it must not
  // reproduce a narrower version of it. The prototype method reads the internal slot
  // directly: it cannot be intercepted and returns the true value.
  a = unwrapBoxed(asCompared(a))
  b = unwrapBoxed(asCompared(b))

  // Identical references or primitives
  if (a === b) return true

  // NaN === NaN (JS gets this wrong)
  if (
    typeof a === 'number' &&
    typeof b === 'number' &&
    isNaN(a as number) &&
    isNaN(b as number)
  ) {
    return true
  }

  // null and undefined are equal to each other
  if ((a === null || a === undefined) && (b === null || b === undefined)) {
    return true
  }

  return false
}

/**
 * Honest inequality — what != should have been.
 *
 * Usage: `a != b` with TjsEquals transforms to `NotEq(a, b)`
 */
export function NotEq(a: unknown, b: unknown): boolean {
  return !Eq(a, b)
}

// ============================================================================
// Legacy equality — bridges back to JavaScript's semantics
// ============================================================================
//
// TJS fixes `==`/`===`, and a fixed operator has no construct to mark, because it is still
// spelled the same. `unsafe` cannot help here: there is nothing at the site to point at.
// So the escape is a NAME. Reaching for one is deliberate, greppable, and the word
// `Legacy` does the teaching — you are asking for the behaviour TJS exists to correct.
//
// THE NAMES ARE DELIBERATELY LONG. `dangerouslySetInnerHTML` is the model: the friction is
// the feature. A short alias (`Leq`, `Lne`) would make the escape as cheap to reach for as
// the safe path, which defeats the point — an escape should cost a moment's thought and be
// obvious in review. Do not add abbreviations.
//
// Where they differ from TJS equality, exactly:
//   NaN vs NaN                    Legacy* false   ·  Eq true (JS gets this wrong)
//   null vs undefined             Legacy* false   ·  Eq true
//   boxed String/Boolean vs prim  Legacy* varies  ·  Eq true (unwraps)

/**
 * JavaScript's `==`, coercion and all.
 *
 * **Dangerous, not merely legacy** — and the name says so on purpose. Coercion invokes
 * `valueOf()`/`toString()` on *any* object operand, so a comparison can run arbitrary user
 * code: it can throw, mutate state, or lie about the value. That is a different order of
 * hazard from the strict pair below, which only differ from TJS over NaN and boxing, and
 * the naming should not flatten the two.
 */
export function DangerousLegacyEquals(a: unknown, b: unknown): boolean {
  return a == b
}

/** JavaScript's `!=` — same coercion hazard as `DangerousLegacyEquals`. */
export function DangerousLegacyNot(a: unknown, b: unknown): boolean {
  return a != b
}

/**
 * JavaScript's `===`: NaN is not itself, and a boxed primitive is not its value.
 *
 * Deliberately NOT named "Dangerous": `===` performs no coercion, so it cannot reach user
 * code. Its warts are wrong answers, not arbitrary execution. Friction proportional to
 * risk — over-warning here would train people to ignore the word where it matters.
 */
export function LegacyExactly(a: unknown, b: unknown): boolean {
  return a === b
}

/** JavaScript's `!==`. */
export function LegacyNotExactly(a: unknown, b: unknown): boolean {
  return a !== b
}

/**
 * JavaScript's parameter-default semantics for an object literal.
 *
 * In TJS an object-literal default is a DICTIONARY: members are defaulted individually,
 * merged on a partial argument, type-checked, and excess keys stripped. `f({x: 5})` against
 * `f(args = {x: 0, y: 0})` yields `{x: 5, y: 0}`.
 *
 * JavaScript instead treats the whole object as one atomic value used only when the
 * argument is `undefined`, so the same call yields `{x: 5}`. When that is genuinely what
 * you want, say so:
 *
 *     function f(args = LegacyDefault({ x: 0, y: 0 })) { … }
 *
 * PER-PARAMETER, which is the point. The previous escape was marking the whole function
 * unsafe with a leading `!` — that disables *all* of its validation, not just the merge,
 * making the escape more destructive than the thing being escaped.
 *
 * Identity at runtime: the marker is compile-time, and the wrapper is what tells the
 * emitter not to build dictionary semantics for this default.
 */
export function LegacyDefault<T>(value: T): T {
  return value
}

/**
 * Check if a value is a TJS error
 */
export function isError(value: unknown): value is TJSError {
  return (
    value !== null &&
    typeof value === 'object' &&
    (value as any).$error === true
  )
}

/**
 * Create a TJS error
 * In debug mode, captures the current call stack
 */
export function error(
  message: string,
  details?: Partial<Omit<TJSError, '$error' | 'message'>>
): TJSError {
  const err: TJSError = {
    $error: true,
    message,
    ...details,
  }

  // Capture call stack when tracking is enabled
  if ((config.callStacks || config.debug) && callStackCount > 0) {
    const currentStack = getStack()
    const fullStack = details?.path
      ? [...currentStack, details.path]
      : currentStack
    err.stack = fullStack
  }

  return err
}

/**
 * Compose multiple errors into a single error
 * Used when multiple parameters have errors
 */
export function composeErrors(errors: TJSError[], funcName?: string): TJSError {
  if (errors.length === 0) {
    return error('Unknown error')
  }
  if (errors.length === 1) {
    return errors[0]
  }

  // Build a message listing all failed parameters
  const paramNames = errors
    .map((e) => {
      // Extract param name from path (e.g., "func.paramName" -> "paramName")
      if (e.path) {
        const parts = e.path.split('.')
        return parts[parts.length - 1]
      }
      return 'unknown'
    })
    .join(', ')

  const message = `Multiple parameter errors in ${
    funcName || 'function'
  }: ${paramNames}`

  return error(message, {
    path: funcName,
    errors,
  })
}

/**
 * Get the type of a value
 *
 * Enhanced typeof that handles:
 * - null (returns 'null' not 'object')
 * - undefined (returns 'undefined')
 * - arrays (returns 'array' not 'object')
 * - native/platform types (returns constructor name for objects)
 *
 * For objects, returns the constructor name which enables pragmatic
 * native type checking (e.g., 'HTMLElement', 'Buffer', 'Event')
 */
export function typeOf(value: unknown): string {
  if (value === null) return 'null'
  if (value === undefined) return 'undefined'
  if (Array.isArray(value)) return 'array'

  const t = typeof value
  if (t !== 'object') return t

  // For objects, return constructor name for pragmatic native type checking
  // This enables checking for HTMLElement, Buffer, Event, etc.
  const constructorName = (value as object).constructor?.name
  if (constructorName && constructorName !== 'Object') {
    return constructorName
  }

  return 'object'
}

/**
 * Check if a value is an instance of a native/platform type by constructor name
 *
 * This enables pragmatic native type checking without shipping type definitions:
 * - isNativeType(el, 'HTMLElement') - DOM element check
 * - isNativeType(buf, 'Buffer') - Node.js Buffer check
 * - isNativeType(evt, 'Event') - DOM Event check
 * - isNativeType(map, 'Map') - Map instance check
 *
 * @param value - The value to check
 * @param typeName - The constructor name to match (e.g., 'HTMLElement', 'Buffer')
 * @returns true if value's constructor.name matches or is in prototype chain
 */
export function isNativeType(value: unknown, typeName: string): boolean {
  if (value === null || value === undefined) return false
  if (typeof value !== 'object' && typeof value !== 'function') return false

  // Check constructor name
  let proto = value
  while (proto !== null) {
    const constructorName = (proto as object).constructor?.name
    if (constructorName === typeName) return true
    proto = Object.getPrototypeOf(proto)
  }

  return false
}

/**
 * A transpiler `TypeDescriptor` (`{ kind: 'integer' }`, the form in every emitted `__tjs` and
 * in `tjs().types`), checked STRICTLY (#3496). `wrap(fn, fn.__tjs)` refused every call,
 * because `checkType` understood only kind NAMES and RuntimeType objects; this is what lets it
 * read the metadata it is documented to take.
 *
 * Every kind is handled explicitly. The exception is a named Type (`declared`,
 * `declared-import`): metadata carries only its NAME, not the Type, so it cannot be checked
 * here. It passes, and the flight recorder says so once per path, because an emitted TJS
 * function already checks it inline and refusing every call would be worse. (The other
 * descriptor checker, `inference.ts`'s `checkType`, passes every kind it does not know;
 * this one does not.)
 */
const unresolvedDeclared = new Set<string>()
function descriptorMatches(value: unknown, d: any, path: string): boolean {
  if (value === null || value === undefined) {
    if (d.nullable || d.kind === 'any') return true
    if (d.kind === 'null') return value === null
    if (d.kind === 'undefined') return value === undefined
    if (d.kind === 'union')
      return (d.members ?? []).some((m: any) =>
        descriptorMatches(value, m, path)
      )
    if (d.kind === 'literal-union')
      return (d.values ?? []).some((v: unknown) => Eq(value, v))
    return false
  }
  switch (d.kind) {
    case 'any':
      return true
    case 'string':
      if (typeof value !== 'string') return false
      if (d.pattern) {
        try {
          return new RegExp(d.pattern, d.flags).test(value)
        } catch {
          return false
        }
      }
      return true
    case 'number':
      return typeof value === 'number'
    case 'integer':
      return typeof value === 'number' && Number.isInteger(value)
    case 'non-negative-integer':
      return typeof value === 'number' && Number.isInteger(value) && value >= 0
    case 'bigint':
      return typeof value === 'bigint'
    case 'boolean':
      return typeof value === 'boolean'
    case 'null':
    case 'undefined':
      return false
    case 'function':
      return typeof value === 'function'
    case 'array':
      return (
        Array.isArray(value) &&
        (!d.items ||
          value.every((v, i) => descriptorMatches(v, d.items, `${path}[${i}]`)))
      )
    case 'object': {
      if (typeof value !== 'object' || Array.isArray(value)) return false
      const shape = d.shape ?? {}
      return Object.entries(shape).every(([k, t]) =>
        descriptorMatches((value as any)[k], t, `${path}.${k}`)
      )
    }
    case 'union':
      return (d.members ?? []).some((m: any) =>
        descriptorMatches(value, m, path)
      )
    case 'literal-union':
      return (d.values ?? []).some((v: unknown) => Eq(value, v))
    case 'declared':
    case 'declared-import': {
      const key = `${path}:${d.typeName}`
      if (!unresolvedDeclared.has(key)) {
        unresolvedDeclared.add(key)
        recorder.record({
          source: 'type',
          severity: 'notice',
          message: `wrap() cannot check the named Type '${d.typeName}' at '${path}' from metadata alone; it is passed unchecked here (an emitted TJS function still checks it inline).`,
        })
      }
      return true
    }
    default:
      return false
  }
}

/** Is `expected` a transpiler TypeDescriptor (and not a kind name or a RuntimeType)? */
function isDescriptor(expected: unknown): boolean {
  return (
    typeof expected === 'object' &&
    expected !== null &&
    typeof (expected as any).kind === 'string' &&
    !('check' in (expected as object))
  )
}

/**
 * Check if a value matches an expected type
 *
 * @param value - The value to check
 * @param expected - A string type name ('string', 'number', etc.), a RuntimeType instance
 *                   with .check(), or a transpiler TypeDescriptor (`{ kind: 'integer' }`,
 *                   as in `__tjs` metadata)
 * @param path - Optional path for error messages
 */
export function checkType(
  value: unknown,
  expected:
    | string
    | { check: (v: unknown) => boolean | string; description: string },
  path?: string
): TJSError | null {
  // If value is already an error, propagate it
  if (isError(value)) return value

  // A transpiler TypeDescriptor, as in emitted `__tjs` metadata (#3496).
  if (isDescriptor(expected)) {
    const d = expected as any
    if (descriptorMatches(value, d, path ?? '')) return null
    const label =
      d.kind === 'declared' || d.kind === 'declared-import'
        ? d.typeName
        : d.kind
    return error(`Expected ${label} for '${path}', got ${typeOf(value)}`, {
      path,
      expected: label,
      actual: typeOf(value),
    })
  }

  // Handle RuntimeType instances (Type() results)
  //
  // A runtime type is a FUNCTION with properties (`asPredicate` in src/types/Type.ts), so
  // `typeof` is 'function', not 'object'. Object-shaped types still match, which is what makes
  // the widening backward compatible with already-emitted output rather than a migration.
  if (
    (typeof expected === 'object' || typeof expected === 'function') &&
    expected !== null &&
    'check' in (expected as object)
  ) {
    const result = expected.check(value)
    if (result === true) return null
    const reason = typeof result === 'string' ? result : undefined
    const msg = reason
      ? `Expected ${expected.description} for '${path}': ${reason}`
      : `Expected ${expected.description} but got ${typeOf(value)}`
    return error(msg, {
      path,
      expected: expected.description,
      actual: typeOf(value),
      reason,
    })
  }

  const actual = typeOf(value)

  // Handle special cases
  if (expected === 'any') return null
  if (expected === actual) return null

  // Number accepts both number types
  if (expected === 'number' && actual === 'number') return null
  if (expected === 'integer' && actual === 'number' && Number.isInteger(value))
    return null
  if (
    expected === 'non-negative-integer' &&
    actual === 'number' &&
    Number.isInteger(value) &&
    (value as number) >= 0
  )
    return null

  // Object matching (basic)
  if (expected === 'object' && actual === 'object') return null

  return error(`Expected ${expected} but got ${actual}`, {
    path,
    // `expected` is narrowed to a string by every branch above, but widening the
    // RuntimeType guard to accept functions left TS unable to see that. It is a string here.
    expected: expected as string,
    actual,
  })
}

/** Type specifier - either a string name or a RuntimeType */
type TypeSpec =
  | string
  | { check: (v: unknown) => boolean | string; description: string }

/**
 * Check that a passed-in function's declared shape matches the expected
 * shape. Returns the function unchanged on a match, or a MonadicError on
 * mismatch. Untyped functions (no `__tjs` metadata — anonymous arrows
 * like `x => false`) pass through unchanged on the assumption that the
 * caller knows what they're doing; they accept any args and return
 * whatever they return.
 *
 * This is a ONE-SHOT check at pass time, NOT a per-call wrapper. The TJS
 * design call: a wrong-shape callback is ONE error at the boundary, not
 * N errors when the receiving function invokes the callback N times.
 *
 * Compatibility rules (deliberately permissive — strict subtyping is a
 * separate, larger feature):
 *   - For each expected param: the actual function may declare fewer
 *     params (extras simply not used). If both declare a kind, they
 *     must match exactly. Either side being `any` always matches.
 *   - For the return type: same exact-match rule when both are known.
 */
export function checkFnShape(
  fn: unknown,
  expectedParamKinds: string[],
  expectedReturnKind: string,
  path: string
): unknown {
  if (typeof fn !== 'function') return fn // outer "is callable" check already ran
  const meta = (fn as any).__tjs
  if (!meta || !meta.params) return fn // untyped — let it run

  const actualEntries = Object.entries(meta.params) as Array<
    [string, { type?: { kind?: string } }]
  >
  for (let i = 0; i < expectedParamKinds.length; i++) {
    const expectedKind = expectedParamKinds[i]
    if (expectedKind === 'any') continue
    const actual = actualEntries[i]
    if (!actual) continue // function takes fewer params, OK
    const actualKind = actual[1]?.type?.kind
    if (!actualKind || actualKind === 'any') continue
    if (actualKind !== expectedKind) {
      return received(
        new MonadicError(
          `Expected (...arg${i}: ${expectedKind}, ...) for '${path}', ` +
            `but callback declares arg${i} as ${actualKind}`,
          `${path}(arg${i})`,
          expectedKind,
          actualKind
        ),
        fn
      )
    }
  }

  if (expectedReturnKind !== 'any' && meta.returns) {
    // Metadata's `returns` is `{ type: TypeDescriptor, defaults?: ... }`,
    // but defensively also accept a bare TypeDescriptor.
    const actualReturnKind = meta.returns.type?.kind ?? meta.returns.kind
    if (
      actualReturnKind &&
      actualReturnKind !== 'any' &&
      actualReturnKind !== expectedReturnKind
    ) {
      return received(
        new MonadicError(
          `Expected callback returning ${expectedReturnKind} for '${path}', ` +
            `but callback returns ${actualReturnKind}`,
          `${path}(return)`,
          expectedReturnKind,
          actualReturnKind
        ),
        fn
      )
    }
  }

  return fn
}

/** Parameter metadata with optional location */
interface ParamMeta {
  type: TypeSpec
  required: boolean
  default?: unknown
  loc?: { start: number; end: number }
}

/**
 * Validate function arguments against __tjs metadata
 * Returns first error found, or null if all valid
 */
export function validateArgs(
  args: Record<string, unknown>,
  meta: {
    params: Record<string, ParamMeta>
  },
  funcName?: string
): TJSError | null {
  for (const [name, param] of Object.entries(meta.params)) {
    const value = args[name]

    // Check if any arg is already an error - propagate first one
    if (isError(value)) return value

    // Check required
    if (param.required && value === undefined) {
      const expectedDesc =
        typeof param.type === 'string' ? param.type : param.type.description
      return error(`Missing required parameter '${name}'`, {
        path: funcName ? `${funcName}.${name}` : name,
        expected: expectedDesc,
        actual: 'undefined',
        loc: param.loc,
      })
    }

    // Skip type check for undefined optional params
    if (value === undefined) continue

    // Type check
    const typeError = checkType(
      value,
      param.type,
      funcName ? `${funcName}.${name}` : name
    )
    if (typeError) {
      // Add location info if available
      if (param.loc) {
        typeError.loc = param.loc
      }
      return typeError
    }
  }

  return null
}

/**
 * Function metadata with safety flags
 */
export interface FunctionMeta {
  params: Record<string, any>
  returns?: { type: any; safe?: boolean; defaults?: Record<string, unknown> }
  /** Function marked with (!) - never validate inputs */
  unsafe?: boolean
  /** Function marked with (?) - always validate inputs */
  safe?: boolean
  /** Return type marked with -! - never validate output */
  unsafeReturn?: boolean
  /** Return type marked with -? - always validate output */
  safeReturn?: boolean
  /** Explicit function name for stack tracking (used when fn.name is empty) */
  name?: string
  /** Polymorphic dispatcher — skip wrapping, dispatch handles validation */
  polymorphic?: boolean
  /**
   * The function takes ONE object whose members `params` lists (a destructured parameter).
   * `isDestructuredParam` is the same flag as `tjs().types` spells it.
   */
  destructured?: boolean
  isDestructuredParam?: boolean
}

/**
 * Determine if we should validate inputs for this call
 * Reserved for future use with runtime validation modes
 */
function _shouldValidateInputs(meta: FunctionMeta): boolean {
  // Per-function flags take precedence
  if (meta.unsafe) return false
  if (meta.safe) return true

  // Block-level override
  if (unsafeDepth > 0) return false

  // Global safety level
  return config.safety !== 'none'
}

/**
 * Determine if we should validate outputs for this call
 * Reserved for future use with runtime validation modes
 */
function _shouldValidateOutputs(meta: FunctionMeta): boolean {
  // No return type declared = no validation
  if (!meta.returns) return false

  // Per-function return flags take precedence
  if (meta.unsafeReturn) return false
  if (meta.safeReturn) return true

  // Block-level override
  if (unsafeDepth > 0) return false

  // Global safety level: 'all' validates outputs, others don't by default
  return config.safety === 'all'
}

/**
 * Wrap a function with monadic type checking
 *
 * @param fn - The original function
 * @param meta - The __tjs metadata
 * @returns Wrapped function that validates inputs and propagates errors
 */
export function wrap<T extends (...args: any[]) => any>(
  fn: T,
  meta: FunctionMeta
): T {
  // Always attach metadata for introspection/autocomplete
  ;(fn as any).__tjs = meta
  // Lazy JSON Schema generation — only computed when called
  ;(fn as any).__tjs.schema = () => functionMetaToJSONSchema(meta)

  // Determine if we need a wrapper at all
  // Polymorphic dispatchers handle their own routing — no wrapping needed
  const needsWrapper =
    !meta.polymorphic && // Has forced safety that requires validation
    (meta.safe ||
      meta.safeReturn ||
      // Global safety requires validation (and not explicitly unsafe)
      (config.safety !== 'none' && !meta.unsafe) ||
      // Has return type that might need validation
      (meta.returns && config.safety === 'all' && !meta.unsafeReturn))

  if (!needsWrapper) {
    return fn
  }

  // Pre-compute flags at wrap time
  const hasReturns = !!meta.returns
  const metaUnsafe = !!meta.unsafe
  const metaSafe = !!meta.safe
  const metaUnsafeReturn = !!meta.unsafeReturn
  const metaSafeReturn = !!meta.safeReturn
  // Pre-compute return defaults (for `key = value` in return type signatures)
  // NOTE: applying defaults adds an Object.assign per call — may need benchmarking
  const returnDefaults = meta.returns?.defaults
  const paramEntries = Object.entries(meta.params)
  const paramCount = paramEntries.length
  // Use meta.name as fallback when fn.name is empty (anonymous functions)
  const funcName = fn.name || meta.name || 'anonymous'

  const wrapped = function (this: any, ...args: Parameters<T>): ReturnType<T> {
    // Fast path: inside unsafe block, skip all validation
    if (unsafeDepth > 0) {
      return fn.apply(this, args)
    }

    // Compute validation flags
    const validateInputs = metaSafe || (!metaUnsafe && config.safety !== 'none')
    const validateOutputs =
      hasReturns &&
      (metaSafeReturn || (!metaUnsafeReturn && config.safety === 'all'))

    // Fast path: no validation needed
    if (!validateInputs && !validateOutputs) {
      return fn.apply(this, args)
    }

    // Fast path: check for error as first arg
    if (args.length > 0 && isError(args[0])) {
      return args[0] as ReturnType<T>
    }

    // Input validation
    if (validateInputs) {
      // Named params only when the metadata SAYS the function destructures one object
      // (#3496). It used to be guessed from the call ("one plain-object argument"), which
      // read `area(r: { w, h })` called with `{ w, h }` as `r: undefined`.
      const isNamedCall =
        !!(meta.destructured || meta.isDestructuredParam) &&
        args.length === 1 &&
        typeof args[0] === 'object' &&
        args[0] !== null &&
        !Array.isArray(args[0])

      // Collect all errors to compose them
      const collectedErrors: TJSError[] = []

      // Fast positional validation (avoids object allocation)
      if (!isNamedCall) {
        for (let i = 0; i < paramCount; i++) {
          const [name, param] = paramEntries[i]
          const value = args[i]

          // Check for error propagation (passed-in errors)
          if (isError(value)) {
            collectedErrors.push(value)
            continue
          }

          // Check required
          if (param.required && value === undefined) {
            collectedErrors.push(
              error(`Missing required parameter '${name}'`, {
                path: `${funcName}.${name}`,
                expected:
                  typeof param.type === 'string'
                    ? param.type
                    : param.type?.description || 'value',
                actual: 'undefined',
                loc: param.loc,
              })
            )
            continue
          }

          // Type check (skip undefined optional)
          if (value !== undefined) {
            const typeErr = checkType(value, param.type, `${funcName}.${name}`)
            if (typeErr) {
              if (param.loc) typeErr.loc = param.loc
              collectedErrors.push(typeErr)
            }
          }
        }
      } else {
        // Named args path (slower, but supports object destructuring)
        const namedArgs = args[0] as Record<string, unknown>
        for (let i = 0; i < paramCount; i++) {
          const [name, param] = paramEntries[i]
          const value = namedArgs[name]

          if (isError(value)) {
            collectedErrors.push(value)
            continue
          }

          if (param.required && value === undefined) {
            collectedErrors.push(
              error(`Missing required parameter '${name}'`, {
                path: `${funcName}.${name}`,
                expected:
                  typeof param.type === 'string'
                    ? param.type
                    : param.type?.description || 'value',
                actual: 'undefined',
                loc: param.loc,
              })
            )
            continue
          }

          if (value !== undefined) {
            const typeErr = checkType(value, param.type, `${funcName}.${name}`)
            if (typeErr) {
              if (param.loc) typeErr.loc = param.loc
              collectedErrors.push(typeErr)
            }
          }
        }
      }

      // If we collected any errors, compose and return them
      if (collectedErrors.length > 0) {
        return composeErrors(collectedErrors, funcName) as ReturnType<T>
      }
    }

    // Track call stack only when enabled (ring buffer, no allocation)
    const trackStack = config.callStacks || config.debug
    if (trackStack) pushStack(funcName)

    try {
      // Execute function
      const result = fn.apply(this, args)

      // Output validation
      if (validateOutputs && meta.returns && !isError(result)) {
        // Apply return defaults before validation
        const validated =
          returnDefaults && typeof result === 'object' && result !== null
            ? Object.assign({}, returnDefaults, result)
            : result
        const returnError = checkType(
          validated,
          meta.returns.type,
          `${funcName}()`
        )
        if (returnError) {
          if (trackStack) popStack()
          return returnError as ReturnType<T>
        }
      }

      if (trackStack) popStack()
      return result
    } catch (e) {
      if (trackStack) popStack()
      // Convert thrown errors to TJS errors
      return error((e as Error).message || String(e), {
        path: funcName,
        cause: e as Error,
      }) as ReturnType<T>
    }
  }

  // Preserve function name and metadata
  Object.defineProperty(wrapped, 'name', { value: fn.name })
  ;(wrapped as any).__tjs = meta
  ;(wrapped as any).__tjs.schema = () => functionMetaToJSONSchema(meta)

  return wrapped as T
}

/**
 * Wrap a class to make it callable without `new`
 *
 * In TJS, classes can be instantiated without the `new` keyword:
 *   const obj = MyClass(args)  // equivalent to new MyClass(args)
 *
 * This eliminates a common source of errors where developers forget `new`.
 * Using explicit `new` still works but should be flagged by the linter.
 */
export function wrapClass<T extends new (...args: any[]) => any>(
  cls: T
): T & ((...args: ConstructorParameters<T>) => InstanceType<T>) {
  // Use a Proxy to intercept both `new Wrapper()` and `Wrapper()` calls
  const wrapped = new Proxy(cls, {
    // Called when using `new Wrapper(...)`
    construct(target, args, newTarget) {
      return Reflect.construct(target, args, newTarget)
    },
    // Called when using `Wrapper(...)` without new
    apply(target, _thisArg, args) {
      return Reflect.construct(target, args)
    },
  })

  // Preserve class name
  Object.defineProperty(wrapped, 'name', { value: cls.name })

  // Copy static properties and methods
  for (const key of Object.getOwnPropertyNames(cls)) {
    if (key !== 'length' && key !== 'name' && key !== 'prototype') {
      Object.defineProperty(
        wrapped,
        key,
        Object.getOwnPropertyDescriptor(cls, key)!
      )
    }
  }

  return wrapped as T & ((...args: ConstructorParameters<T>) => InstanceType<T>)
}

// ============================================================================
// SafeFunction and Eval - moved to ./eval.ts
// ============================================================================
//
// Eval and SafeFunction are in a separate module to keep the runtime lite.
// Import from 'tjs-lang/eval' when you need dynamic code execution.
//
// Runtime (this file): ~5KB gzipped - type checking, Is/IsNot, wrap, etc.
// Eval module:         ~27KB gzipped - adds transpiler + VM for dynamic code
// ============================================================================

/**
 * Create an isolated TJS runtime instance
 *
 * Each call returns a fresh runtime with its own:
 * - config (debug, safety, etc.)
 * - callStack
 * - unsafeDepth
 *
 * The new instance inherits the current global config at creation time,
 * but subsequent changes are isolated.
 *
 * Use this to prevent state leaking between transpiled modules.
 */
export function createRuntime() {
  // Per-instance state - inherit current global config
  let instanceConfig: TJSConfig = { ...config }
  const instStackSize = instanceConfig.maxStackSize ?? STACK_SIZE
  const instanceStackBuffer: string[] = new Array(instStackSize).fill('')
  let instanceStackHead = 0
  let instanceStackCount = 0
  const instanceRecorder = createRecorder(() => instanceConfig, globalMirror)
  let instanceUnsafeDepth = 0

  // Per-instance stateful functions
  function instanceConfigure(options: TJSConfig): void {
    instanceConfig = { ...instanceConfig, ...options }
    warnLateConfigureOnce()
  }

  function instanceGetConfig(): TJSConfig {
    return { ...instanceConfig }
  }

  function instancePushStack(name: string): void {
    if ((instanceConfig.callStacks || instanceConfig.debug) && name) {
      instanceStackBuffer[instanceStackHead] = name
      instanceStackHead = (instanceStackHead + 1) % instStackSize
      if (instanceStackCount < instStackSize) instanceStackCount++
    }
  }

  function instancePopStack(): void {
    if (
      (instanceConfig.callStacks || instanceConfig.debug) &&
      instanceStackCount > 0
    ) {
      instanceStackHead =
        (instanceStackHead - 1 + instStackSize) % instStackSize
      instanceStackCount--
    }
  }

  function instanceGetStack(): string[] {
    if (instanceStackCount === 0) return []
    const result: string[] = []
    const start =
      (instanceStackHead - instanceStackCount + instStackSize) % instStackSize
    for (let i = 0; i < instanceStackCount; i++) {
      result.push(instanceStackBuffer[(start + i) % instStackSize])
    }
    return result
  }

  function instanceResetRuntime(): void {
    instanceConfig = { ...DEFAULT_CONFIG }
    instanceStackHead = 0
    instanceStackCount = 0
    instanceRecorder.reset()
    instanceUnsafeDepth = 0
  }

  function instanceEnterUnsafe(): void {
    instanceUnsafeDepth++
  }

  function instanceExitUnsafe(): void {
    if (instanceUnsafeDepth > 0) instanceUnsafeDepth--
  }

  function instanceIsUnsafeMode(): boolean {
    return instanceUnsafeDepth > 0
  }

  // Extension registry: typeName -> methodName -> fn
  const extensionRegistry = new Map<
    string,
    Map<string, (...args: any[]) => any>
  >()

  function instanceRegisterExtension(
    typeName: string,
    methodName: string,
    fn: (...args: any[]) => any
  ): void {
    if (!extensionRegistry.has(typeName)) {
      extensionRegistry.set(typeName, new Map())
    }
    extensionRegistry.get(typeName)!.set(methodName, fn)
    // `asCompared` deliberately does NOT feed a process-wide table.
    //
    // It used to, and that made projections GLOBAL: a module with no `extend` at all had
    // its `if (x)` silently change when an unrelated module loaded. Reproduced —
    // "B before A loads: truthy / B after A loads: FALSY". Four shipped documents claimed
    // the opposite. The file-local table the emitter writes (`__ac`) is the whole
    // mechanism; a shared mutable type->behaviour table is the prototype pollution this
    // feature exists to avoid.
  }

  function instanceResolveExtension(
    value: unknown,
    methodName: string
  ): ((...args: any[]) => any) | undefined {
    // Determine type name from value
    const t = typeof value
    let typeName: string
    if (value === null || value === undefined) return undefined
    if (t === 'string') typeName = 'String'
    else if (t === 'number') typeName = 'Number'
    else if (t === 'boolean') typeName = 'Boolean'
    else if (Array.isArray(value)) typeName = 'Array'
    else if (t === 'object') {
      // Check constructor name for class instances (including DOM classes)
      typeName = (value as any).constructor?.name || 'Object'
    } else {
      return undefined
    }

    // Walk prototype chain: HTMLInputElement -> HTMLElement -> Element -> Object
    let current: string | undefined = typeName
    while (current) {
      const methods = extensionRegistry.get(current)
      if (methods?.has(methodName)) {
        return methods.get(methodName)
      }
      // Walk up: try parent class (for DOM/custom class hierarchies)
      if (t === 'object' && !Array.isArray(value)) {
        const proto = Object.getPrototypeOf(
          current === typeName ? value : Object.getPrototypeOf(value)
        )
        current = proto?.constructor?.name
        if (current === 'Object' || current === typeName) break
      } else {
        break
      }
    }

    // Fallback: check 'Object' extensions
    const objectMethods = extensionRegistry.get('Object')
    if (objectMethods?.has(methodName)) {
      return objectMethods.get(methodName)
    }

    return undefined
  }

  function instanceTypeError(
    path: string,
    expected: string,
    value: unknown,
    _reason?: string,
    root?: unknown
  ): MonadicError {
    // Already a TJS error: propagate it (see `typeError`, including `root`).
    if (root !== undefined && isMonadicError(root)) return root
    if (isMonadicError(value)) return value
    const carried = findCarriedError(root !== undefined ? root : value)
    if (carried) return carried
    // `typeof []` is 'object', which made every array failure report "got object" — least
    // helpful exactly where arrays are a headline feature (`xs: [0]`). `Array.isArray` is
    // the only honest answer here.
    const actual = describeActual(value)
    const stack =
      instanceConfig.callStacks || instanceConfig.debug
        ? instanceGetStack()
        : undefined
    const err = received(
      new MonadicError(
        `Expected ${expected} for '${path}', got ${actual}`,
        path,
        expected,
        actual,
        stack
      ),
      root !== undefined ? root : value
    )

    // Track in the flight recorder
    instanceRecorder.record({
      source: 'type',
      severity: 'error',
      message: err.message,
      error: err,
      stack,
    })

    if (instanceConfig.logTypeErrors) {
      console.error(`[TJS TypeError] ${err.message}`)
    }
    if (instanceConfig.throwTypeErrors) {
      throw err
    }

    return err
  }

  function instanceErrors(): MonadicError[] {
    return instanceRecorder.errors()
  }

  function instanceClearErrors(): MonadicError[] {
    const cleared = instanceRecorder.errors()
    instanceRecorder.clear()
    return cleared
  }

  function instanceGetErrorCount(): number {
    return instanceRecorder.getErrorCount()
  }

  function instanceError(
    message: string,
    details?: Partial<Omit<TJSError, '$error' | 'message'>>
  ): TJSError {
    const err: TJSError = {
      $error: true,
      message,
      ...details,
    }
    if (
      (instanceConfig.callStacks || instanceConfig.debug) &&
      instanceStackCount > 0
    ) {
      const fullStack = details?.path
        ? [...instanceGetStack(), details.path]
        : instanceGetStack()
      err.stack = fullStack
    }
    return err
  }

  /**
   * Bang access (!.) — asserted non-null member access.
   * Returns MonadicError if obj is null, undefined, or already a MonadicError.
   * Otherwise returns obj[prop] (bare access — throws as usual on other errors).
   */
  function instanceBang(obj: unknown, prop: string): unknown {
    if (obj === null || obj === undefined) {
      return instanceTypeError(`bang.${prop}`, 'non-null', obj)
    }
    if (isMonadicError(obj)) return obj
    return (obj as any)[prop]
  }

  return {
    version: TJS_VERSION,
    abi: RUNTIME_ABI,
    // Monadic error handling
    MonadicError,
    typeError: instanceTypeError,
    isMonadicError,
    // Bang access (!.)
    bang: instanceBang,
    // Legacy error handling
    isError,
    error: instanceError,
    composeErrors,
    typeOf,
    isNativeType,
    checkType,
    validateArgs,
    wrap,
    checkFnShape,
    wrapClass,
    compareVersions,
    versionsCompatible,
    // Create child runtime instances. Emitted/converted modules capture their
    // runtime via THIS method (`globalThis.__tjs.createRuntime()`) at module-eval
    // time — whereas the top-level install calls the bare module-level
    // `createRuntime()`. Flag the former so a later configure() knows a module
    // already snapshotted its config and can warn it won't reach it (#23).
    createRuntime: capturingCreateRuntime,
    // Debug mode (instance-specific)
    configure: instanceConfigure,
    getConfig: instanceGetConfig,
    pushStack: instancePushStack,
    popStack: instancePopStack,
    getStack: instanceGetStack,
    errors: instanceErrors,
    clearErrors: instanceClearErrors,
    getErrorCount: instanceGetErrorCount,
    // Flight recorder — the whole stream, not just type errors
    record: instanceRecorder.record,
    records: instanceRecorder.records,
    clearRecords: instanceRecorder.clear,
    getRecordCount: instanceRecorder.getRecordCount,
    getDroppedCount: instanceRecorder.getDroppedCount,
    resetRuntime: instanceResetRuntime,
    // Unsafe mode (instance-specific)
    enterUnsafe: instanceEnterUnsafe,
    exitUnsafe: instanceExitUnsafe,
    isUnsafeMode: instanceIsUnsafeMode,
    // Type system
    validate,
    infer: s.infer.bind(s),
    /**
     * `infer`, with every object left OPEN to extra keys.
     *
     * `s.infer` emits `additionalProperties: false`, which tosijs-schema 1.5.0 began
     * enforcing — so emitted type guards silently started rejecting values carrying an
     * extra key. Excess keys are fine in TJS (decided 2026-08-14); `infer` itself keeps
     * the faithful schema, because `Schema()` and JSON-Schema export want it.
     */
    inferOpen: (value: unknown) => openInferredShapes(s.infer(value)),
    Type,
    isRuntimeType,
    Union,
    Exactly,
    Generic,
    Enum,
    FunctionPredicate,
    Nullable,
    Optional,
    TArray,
    TString,
    TNumber,
    TBoolean,
    TInteger,
    TPositiveInt,
    TNonEmptyString,
    TEmail,
    TUrl,
    TUuid,
    TPair,
    TRecord,
    // Structural equality
    Is,
    IsNot,
    // Honest equality (== / != with TjsEquals)
    Eq,
    NotEq,
    DangerousLegacyEquals,
    DangerousLegacyNot,
    LegacyExactly,
    LegacyNotExactly,
    LegacyDefault,
    // Honest typeof (typeof with TjsEquals)
    TypeOf,
    // Honest truthiness (unwraps boxed primitives)
    toBool,
    // switch comparison keys (native .tjs) — see swKey
    swKey,
    tjsEquals,
    // Extensions
    registerExtension: instanceRegisterExtension,
    resolveExtension: instanceResolveExtension,
  }
}

/** Type for runtime instances */
export type TJSRuntime = ReturnType<typeof createRuntime>

/**
 * TJS Runtime object - attached to globalThis.__tjs
 *
 * NOTE: This is a shared global instance. For isolated execution,
 * use createRuntime() instead.
 */
export const runtime = {
  version: TJS_VERSION,
  abi: RUNTIME_ABI,
  // Monadic error handling (new)
  MonadicError,
  typeError,
  isMonadicError,
  // Legacy error handling (deprecated)
  isError,
  error,
  composeErrors,
  typeOf,
  isNativeType,
  checkType,
  validateArgs,
  wrap,
  checkFnShape,
  wrapClass,
  compareVersions,
  versionsCompatible,
  // Debug mode
  configure,
  getConfig,
  pushStack,
  popStack,
  getStack,
  errors,
  clearErrors,
  getErrorCount,
  // Flight recorder — the whole stream, not just type errors
  record,
  records,
  clearRecords,
  getRecordCount,
  getDroppedCount,
  resetRuntime,
  // Unsafe mode
  enterUnsafe,
  exitUnsafe,
  isUnsafeMode,
  // Factory for isolated instances
  createRuntime: capturingCreateRuntime,
  // Type system (used by transpiled Type declarations)
  validate,
  infer: s.infer.bind(s),
  /** See the instance runtime's `inferOpen`. */
  inferOpen: (value: unknown) => openInferredShapes(s.infer(value)),
  Type,
  isRuntimeType,
  Union,
  Exactly,
  Generic,
  Enum,
  FunctionPredicate,
  Nullable,
  Optional,
  TArray,
  // Built-in types
  TString,
  TNumber,
  TBoolean,
  TInteger,
  TPositiveInt,
  TNonEmptyString,
  TEmail,
  TUrl,
  TUuid,
  Timestamp,
  TimestampISO,
  LegalDate,
  TPair,
  TRecord,
  // Structural equality (used by Is/IsNot operators)
  Is,
  IsNot,
  // Honest equality (used by == and != with TjsEquals)
  Eq,
  NotEq,
  DangerousLegacyEquals,
  DangerousLegacyNot,
  LegacyExactly,
  LegacyNotExactly,
  LegacyDefault,
  // Honest typeof (used by typeof with TjsEquals)
  TypeOf,
  // Honest truthiness (used in TjsStandard for boxed-primitive coercion)
  toBool,
  // switch comparison keys (native .tjs)
  swKey,
}

/**
 * Install runtime globally (idempotent, version-checked)
 *
 * Version handling:
 * - Same version: reuse existing (no warning)
 * - Compatible (same major): reuse existing, log info
 * - Incompatible (different major): warn, use newer version
 */
export function installRuntime(): typeof runtime {
  const g = globalThis as any

  if (g.__tjs) {
    const existingVersion = g.__tjs.version

    // Guard against polluted __tjs without proper version
    if (typeof existingVersion !== 'string') {
      g.__tjs = runtime
      return runtime
    }

    const comparison = compareVersions(TJS_VERSION, existingVersion)

    if (comparison === 0) {
      // Exact same version - just reuse
      return g.__tjs
    }

    if (versionsCompatible(TJS_VERSION, existingVersion)) {
      // Same major version - compatible, use newer
      if (comparison > 0) {
        console.info(
          `TJS runtime: upgrading ${existingVersion} → ${TJS_VERSION}`
        )
        g.__tjs = runtime
      } else {
        console.info(
          `TJS runtime: keeping ${existingVersion} (newer than ${TJS_VERSION})`
        )
      }
    } else {
      // Incompatible (a different major, or a different minor below 1.0)
      console.warn(
        `TJS runtime version conflict: ${existingVersion} vs ${TJS_VERSION} (incompatible versions)`
      )
      // Use the newer one but warn about potential issues
      if (comparison > 0) {
        console.warn(`Upgrading to ${TJS_VERSION} - check for breaking changes`)
        g.__tjs = runtime
      }
    }

    return g.__tjs
  }

  g.__tjs = runtime
  return runtime
}

/**
 * Generate runtime wrapper code for emitted JS
 * Skips wrapping for unsafe functions (marked with !)
 */
export function emitRuntimeWrapper(funcName: string): string {
  return `
// TJS runtime wrapper (skips unsafe functions)
if (typeof ${funcName}.__tjs === 'object' && !${funcName}.__tjs.unsafe && typeof globalThis.__tjs?.wrap === 'function') {
  ${funcName} = globalThis.__tjs.wrap(${funcName}, ${funcName}.__tjs)
}
`.trim()
}

/**
 * Generate class wrapper code for emitted JS
 * Makes classes callable without `new` keyword
 * Emits standalone JS - no runtime dependency
 */
export function emitClassWrapper(className: string): string {
  return `
// TJS: callable without new
${className} = new Proxy(${className}, { apply(t, _, a) { return Reflect.construct(t, a) } });
`.trim()
}

/**
 * Questions/Notes:
 *
 * Q1: Should wrap() be automatic or opt-in?
 *     Current: Must be explicitly wrapped
 *     Could: Auto-wrap all functions with __tjs metadata
 *
 * Q2: Performance overhead of validation?
 *     Need benchmarks comparing wrapped vs unwrapped
 *     Could have 'production' mode that skips validation
 *
 * Q3: Async function handling?
 *     wrap() should detect async and handle Promise returns
 *
 * Q4: Deep object validation?
 *     Currently only checks top-level type
 *     Could recursively validate object shapes
 */
